import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, isTouchDevice, TextField } from "@shapr3d/frontend-lib";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { useCreateTeamSpace } from "../../hooks/useTeamSpaces";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { Modal } from "../../lib/modal";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";

import style from "./SpaceCreationModal.module.css";

type SpaceCreationFormData = { name: string };

export default function SpaceCreationModal({
  open = true,
  onClose,
  name,
}: {
  open?: boolean;
  onClose: () => void;
  name?: string;
}) {
  const { t, m } = useTranslation();
  const schema = useMemo(
    () =>
      z.object({
        name: z.string().nonempty(t(m.TeamSelector.PleaseEnterSpaceName)),
      }),
    [t, m]
  );
  const { teamID } = useParams();
  const tracker = useTracker();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<SpaceCreationFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name },
  });

  useEffect(() => {
    if (open) {
      reset({ name });
    }
  }, [open, name, reset]);

  const { mutateAsync, error } = useCreateTeamSpace(teamID);

  const submit = async (data: SpaceCreationFormData) => {
    if (!teamID) {
      return;
    }
    try {
      await mutateAsync({ teamID, name: data.name });
      onClose();
      tracker.trackButtonClick({
        action: ElementClickAction.CONFIRM,
        location: ElementClickLocation.SPACE_CREATION_MODAL,
      });
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  let errorMessage = null;
  if (error) {
    errorMessage = t(m.ErrorMessage.Unknown);
  }

  return (
    <Modal
      open={open}
      title={t(m.TeamSelector.CreateNewSpace)}
      size="small"
      onClose={onClose}
      telemetryName="space_creation"
    >
      <form className={style.teamNameChangeForm} onSubmit={handleSubmit(submit)}>
        <TextField
          {...register("name")}
          placeholder={t(m.TeamSelector.SpaceName)}
          errorMessage={errors.name?.message}
          maxLength={200}
          disabled={isSubmitting}
          autoFocus={!isTouchDevice()}
        ></TextField>
        {errorMessage && <div className={style.error}>{errorMessage}</div>}
        <div className={style.buttonContainer}>
          <Button type="submit" disabled={isSubmitting}>
            {t(m.TeamSelector.Save)}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
